import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { fulfillmentMethod, shipToId, pickUpWarehouse } = state.context.session;
    const shipTo = getShipToState(state, shipToId).value;
    const pageType = getCurrentPage(state).type;

    return {
        fulfillmentMethod,
        shipTo,
        pickUpWarehouse,
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        isSticky: state.xHeader.isSticky,
        isHomePage: pageType === "HomePage", // HomePage
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
        enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,

    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "IconClear",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: flex;
            align-items: center;
            * {
                ${({ isSticky, isHomePage, toggleHeaderColor }: any) => {
                    if (isSticky || !isHomePage || toggleHeaderColor) {
                        return `
                        color: #000 !important;
                        `;
                    }
                }}
            }
        `,
    },
    titleClickable: {
        css: css`
            color: ${getColor("common.white")};
            font-weight: 500;
            font-size: 12px;
        `,
    },
    titleTypography: {
        css: css`
            font-size: 12px;
            color: ${getColor("common.white")};
            font-weight: 500;

            &.shipTo_Pickup-value {
                color ${getColor("common.ui5")};
            }
        `,
        ellipsis: true,
        transform: "capitalize",
    },
    drawer: {
        cssOverrides: {
            drawerContent: css`
                height: 100%;
                overflow-y: inherit;
            `,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: 375px;
                overflow-x: inherit;
                overflow-y: inherit;
                top: 50%;
                transform: translateY(-50%);
                padding: 20px 50px 20px 50px;
                height: auto;
                min-height: 260px;
            `,
        },
    },
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentMethod,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    shipTo,
    pickUpWarehouse,
    isSticky,
    isHomePage,
    toggleHeaderColor,
    enableWarehousePickup
}) => {
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    if(!enableWarehousePickup) {
        return <></>
    }

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper} {...({ isSticky, isHomePage, toggleHeaderColor } as any)}>
                <Typography {...styles.titleTypography}>{translate("Fulfillment:")}&nbsp;</Typography>
                <Typography {...styles.titleTypography} className="shipTo_Pickup-value">
                    {fulfillmentMethod === FulfillmentMethod.PickUp ? translate("Pickup at") : translate("Ship")}&nbsp;
                </Typography>
                <Typography translate="no" {...styles.titleTypography} className="shipTo_Pickup-value">
                    {fulfillmentMethod === FulfillmentMethod.PickUp
                        ? pickUpWarehouse?.description
                        : shipTo?.companyName}
                    &nbsp;
                </Typography>
                <Clickable
                    {...styles.titleClickable}
                    onClick={handleClickAddress}
                    data-test-selector="header_shipToButton"
                >
                    {translate("Change")}
                </Clickable>
            </StyledWrapper>
            <Drawer
                {...drawerStyles}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
